import React, { useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { CreateDrawer } from "@/components/common/CreateDrawer";
import { Skeletons } from "@/components/common/EmptyState";
import { useResource } from "@/hooks/useResource";
import { Button } from "@/components/ui/button";
import { CheckSquare, Plus, User } from "@phosphor-icons/react";

const STATUSES = ["To do", "In progress", "Under review", "Blocked", "Completed"];
const FIELDS = [
  { name: "title", label: "Task Title" },
  { name: "brand", label: "Brand", type: "select" },
  { name: "priority", label: "Priority", type: "select", options: ["High", "Medium", "Low"] },
  { name: "status", label: "Status", type: "select", options: STATUSES },
  { name: "deadline", label: "Deadline", type: "date" },
];

export default function Tasks() {
  const { items, isLoading, create, update } = useResource("tasks");
  const [open, setOpen] = useState(false);
  const [dragId, setDragId] = useState(null);

  return (
    <div>
      <PageHeader title="Tasks" subtitle="Team workflow across every campaign" icon={CheckSquare}
        actions={<Button onClick={() => setOpen(true)} data-testid="add-btn" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white"><Plus size={16} weight="bold" className="mr-1" /> New Task</Button>} />

      {isLoading ? <Skeletons /> : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STATUSES.map((status) => {
            const col = items.filter((t) => t.status === status);
            return (
              <div key={status} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragId) { update.mutate({ id: dragId, body: { status } }); setDragId(null); } }}
                className="w-72 shrink-0 rounded-2xl border border-border bg-surface p-3" data-testid={`task-col-${status.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="mb-3 flex items-center justify-between px-1"><span className="text-sm font-bold">{status}</span><span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{col.length}</span></div>
                <div className="space-y-3">
                  {col.map((t) => (
                    <div key={t.id} draggable onDragStart={() => setDragId(t.id)} data-testid="tasks-card" className="cursor-grab rounded-xl border border-border bg-surface-raised p-4">
                      <div className="mb-2 flex items-center justify-between"><StatusBadge priority={t.priority} /><span className="text-[10px] text-muted-foreground">{new Date(t.deadline).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span></div>
                      <p className="text-sm font-semibold leading-snug">{t.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{t.brand} · {t.campaign}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="flex items-center gap-1 text-xs text-muted-foreground"><User size={12} /> {t.owner?.split(" ")[0]}</span>
                        {t.checklist_total > 0 && <span className="text-[10px] text-muted-foreground">{t.checklist_done}/{t.checklist_total}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreateDrawer open={open} onOpenChange={setOpen} title="Task" fields={FIELDS} submitting={create.isPending}
        onSubmit={(body) => create.mutateAsync({ ...body, checklist_done: 0, checklist_total: 0 })} />
    </div>
  );
}
